import { Instant, LocalDate } from "@js-joda/core";
import BloodGlucoseThresholds from "./BloodGlucoseThresholds";
import BloodPressureThresholds from "./BloodPressureThresholds";
import { Caregiver } from "./Caregiver.ts";
import {
  PersonalizedDiabetesClinicalProtocol,
  PersonalizedHypertensionClinicalProtocol,
} from "./ClinicalProtocol.ts";
import Department from "./Department.ts";
import {
  DiabetesPatientApiDto,
  hasDiabetes,
  hasHypertension,
  HypertensionPatientApiDto,
} from "./PatientApiDto";
import PatientStatus from "./PatientStatus";

export type Patient = {
  id: string;
  medicalRecordNumber: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  estimatedDateOfDelivery?: LocalDate;
  diagnosis?: string;
  lastReviewTime?: Instant;
  firstReadingTime?: Instant;
  status: PatientStatus;
  prescribingCaregiverDepartment?: Department;
  prescribingCaregiver: Caregiver;
};

type HypertensionFields = {
  prescriptionId: string;
  unreviewedReadings: UnreviewedHypertensionReadings;
  thresholds?: BloodPressureThresholds;
  deliveryDate?: LocalDate;
  personalizedClinicalProtocol: PersonalizedHypertensionClinicalProtocol;
};

type DiabetesFields = {
  prescriptionId: string;
  unreviewedReadings: UnreviewedDiabetesReadings;
  thresholds?: BloodGlucoseThresholds;
  thresholdsLastUpdated?: { at: Instant; by: Caregiver };
  deliveryDate?: LocalDate;
  personalizedClinicalProtocol: PersonalizedDiabetesClinicalProtocol;
};

export type HypertensionPatient = Patient & HypertensionFields & { hasDiabetes: boolean };
export type DiabetesPatient = Patient & DiabetesFields & { hasHypertension: boolean };

export type UnreviewedHypertensionReadings = {
  total: number;
  low: number;
  acceptable: number;
  elevated: number;
  critical: number;
};

export type UnreviewedDiabetesReadings = {
  total: number;
  emergencyLow: number;
  criticalLow: number;
  low: number;
  acceptable: number;
  elevated: number;
  criticalHigh: number;
  pending: number;
  unclassified: number;
};

export const toHypertensionPatient = <T>(
  dto: T & HypertensionPatientApiDto
): T & HypertensionPatient => ({
  ...dto,
  estimatedDateOfDelivery: dto.estimatedDateOfDelivery
    ? LocalDate.parse(dto.estimatedDateOfDelivery)
    : undefined,
  ...dto.hypertension,
  thresholds: dto.hypertension.personalizedClinicalProtocol.thresholds,
  lastReviewTime: parseInstant(dto.hypertension.lastReviewTime),
  firstReadingTime: parseInstant(dto.hypertension.firstReadingTime),
  hasDiabetes: hasDiabetes(dto),
  deliveryDate: dto.hypertension.deliveryDate
    ? LocalDate.parse(dto.hypertension.deliveryDate)
    : undefined,
});

export const toDiabetesPatient = <T>(dto: T & DiabetesPatientApiDto): T & DiabetesPatient => {
  const thresholds = dto.diabetes.personalizedClinicalProtocol.thresholds;
  const lastUpdatedAt = thresholds?.lastUpdatedAt;
  const lastUpdatedBy = thresholds?.lastUpdatedBy;

  return {
    ...dto,
    estimatedDateOfDelivery: dto.estimatedDateOfDelivery
      ? LocalDate.parse(dto.estimatedDateOfDelivery)
      : undefined,
    ...dto.diabetes,
    thresholds: thresholds,
    thresholdsLastUpdated:
      lastUpdatedAt && lastUpdatedBy
        ? {
            at: Instant.parse(lastUpdatedAt),
            by: lastUpdatedBy,
          }
        : undefined,
    lastReviewTime: parseInstant(dto.diabetes.lastReviewTime),
    firstReadingTime: parseInstant(dto.diabetes.firstReadingTime),
    hasHypertension: hasHypertension(dto),
    deliveryDate: dto.diabetes.deliveryDate
      ? LocalDate.parse(dto.diabetes.deliveryDate)
      : undefined,
  };
};

const parseInstant = (instant: string | undefined) =>
  instant ? Instant.parse(instant) : undefined;

export type PatientOffboardingReason =
  | "device-connectivity-issue"
  | "patient-completed-protocol"
  | "patient-end-program-early"
  | "patient-non-compliant"
  | "patient-transferred"
  | "patient-declined"
  | "other";

export type OffboardingRequest = {
  prescriptionId: string;
  reason: PatientOffboardingReason;
  deliveryDate?: LocalDate;
  note?: string;
};
